import { graphql } from 'gatsby';
import React, { FC } from 'react';
import styled from 'styled-components';
import { RemoteWorkQuery } from '../../../graphql-types';
import { PageForm } from '../../components/Form';
import { TopicCard } from '../../components/Home/TopicCard';
import { Layout, PageHero, Section } from '../../components/Layout';
import { RemoteWorkers } from '../../components/RemoteWorkers';
import SEO from '../../components/SEO';
import { Strong } from '../../components/Strong';
import { AuthorsGrid } from '../../components/Topic';
import { floor10 } from '../../utils/format';
import { floralwhite, white } from '../../utils/styles';
import { rhythm } from '../../utils/typography';

const TopicDetails = styled.div`
  p:nth-child(3) {
    margin: 0;
  }
`;

const TopicDescription = styled.p`
  margin: 0;
`;

const BannerSection = styled(Section)`
  padding-top: ${rhythm(2)};
`;

type RemoteWorkProps = {
  data: RemoteWorkQuery;
};

const RemoteWork: FC<RemoteWorkProps> = ({ data }) => {
  const authorsCount = data.authors.totalCount;
  const publicationsCount = floor10(data.publications.totalCount);
  return (
    <Layout>
      <SEO title="Remote Work" />
      <PageHero
        image={<RemoteWorkers />}
        content={
          <TopicDetails>
            <h1>
              Have you figured out <Strong>remote work</Strong>?
            </h1>
            <p>
              We had such a horrible experience being thrown into working remotely that we wanted to help others take
              control of this setup as quickly as possible.
            </p>
            <p>
              Here you can browse our summaries of <Strong>{publicationsCount}+</Strong> resources about remote work
              created by <Strong>{authorsCount}</Strong> experienced leaders.
            </p>
          </TopicDetails>
        }
      />
      <Section>
        <AuthorsGrid authors={data.authors} />
      </Section>
      <BannerSection color={floralwhite}>
        <TopicCard title="Are you happy with your home office setup?" url="/remote-work/gear">
          <TopicDescription>Shop the best Black Friday deals for working from home effectively.</TopicDescription>
        </TopicCard>
      </BannerSection>
      <Section color={floralwhite}>
        <PageForm
          endpoint="author"
          title="Have we missed anyone?"
          subtitle="Tell us who you'd like to see in this list. We’ll hand pick their best resources about remote work."
          fields={[
            {
              name: 'author',
              label: "I'd like to hear from",
              type: 'text',
              initialValue: '',
              placeholder: 'Who do you want to hear from?',
            },
            {
              name: 'email',
              label: 'Send all the gold to',
              type: 'email',
              initialValue: '',
              placeholder: 'Your email here...',
            },
          ]}
        />
      </Section>
    </Layout>
  );
};

export default RemoteWork;

export const pageQuery = graphql`
  query RemoteWork {
    authors: allAirtableRemoteWorkAuthors(sort: { order: ASC, fields: data___weight }) {
      totalCount
      edges {
        node {
          id
          data {
            name
            firstName
            lastName
            jobTitle
            slug
            publications {
              id
            }
            picture {
              localFiles {
                childImageSharp {
                  fluid(maxWidth: 150) {
                    ...GatsbyImageSharpFluid
                  }
                }
              }
            }
          }
        }
      }
    }
    publications: allAirtableRemoteWorkPublications {
      totalCount
    }
  }
`;
